/**
 * Domain 6: Attention & Processing Speed (الانتباه وسرعة المعالجة)
 * 8 Modular Template Families for perceptual acuity, symbol search, and rapid matching
 */

import { renderOptionSvg, DrawShapes } from './svg-builder.js';

export const SPEED_TEMPLATES = [
  // 1. Symbol Oddity Search (Find the intruder in a 4x4 matrix)
  {
    id: 'speed_symbol_oddity_search',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 2) {
      const symbolPairs = [
        { common: '◆', odd: '◇', name_ar: 'المعين المفرغ (◇)', name_en: 'hollow diamond (◇)', common_ar: 'المعينات المصمتة (◆)', common_en: 'solid diamonds (◆)' },
        { common: '●', odd: '○', name_ar: 'الدائرة المفرغة (○)', name_en: 'hollow circle (○)', common_ar: 'الدوائر المصمتة (●)', common_en: 'solid circles (●)' },
        { common: '■', odd: '□', name_ar: 'المربع المفرغ (□)', name_en: 'hollow square (□)', common_ar: 'المربعات المصمتة (■)', common_en: 'solid squares (■)' },
        { common: '▲', odd: '△', name_ar: 'المثلث المفرغ (△)', name_en: 'hollow triangle (△)', common_ar: 'المثلثات المصمتة (▲)', common_en: 'solid triangles (▲)' },
        { common: '★', odd: '☆', name_ar: 'النجمة المفرغة (☆)', name_en: 'hollow star (☆)', common_ar: 'النجوم المصمتة (★)', common_en: 'solid stars (★)' }
      ];
      const chosenPair = prng.pick(symbolPairs);
      const commonSym = chosenPair.common;
      const oddSym = chosenPair.odd;

      const oddRow = prng.int(0, 3);
      const oddCol = prng.int(0, 3);

      let gridSvg = `<rect x="0" y="0" width="220" height="160" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>`;
      for (let r = 0; r < 4; r++) {
        for (let c = 0; c < 4; c++) {
          const isOdd = (r === oddRow && c === oddCol);
          const x = 35 + c * 45;
          const y = 35 + r * 35;
          gridSvg += `<text x="${x}" y="${y}" fill="${isOdd ? '#3b82f6' : 'currentColor'}" font-size="20" font-weight="bold" text-anchor="middle">${isOdd ? oddSym : commonSym}</text>`;
        }
      }

      const stimulusSvg = `<svg class="iq-svg-oddity" viewBox="0 0 220 160" xmlns="http://www.w3.org/2000/svg">${gridSvg}</svg>`;

      const rowNames_ar = ['الصف الأول (العلوي)', 'الصف الثاني', 'الصف الثالث', 'الصف الرابع (السفلي)'];
      const rowNames_en = ['Row 1 (Top)', 'Row 2', 'Row 3', 'Row 4 (Bottom)'];

      const allChoices = prng.shuffle([0, 1, 2, 3]);
      const correctIndex = allChoices.indexOf(oddRow);

      const options = allChoices.map((rIdx, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: rowNames_ar[rIdx],
        text_en: rowNames_en[rIdx]
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ابحث سريعًا: في أي صف يقع الرمز المختلف ${chosenPair.name_ar} بين ${chosenPair.common_ar}؟`,
        questionText_en: `Search rapidly: In which row is the single intruder symbol ${chosenPair.name_en} located among the ${chosenPair.common_en}?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `يقع الرمز المختلف في (${rowNames_ar[oddRow]})، العمود ${oddCol + 1}.`,
        explanation_en: `The intruder symbol is located in (${rowNames_en[oddRow]}), Column ${oddCol + 1}.`
      };
    }
  },

  // 2. Rapid Pair Comparison (Identical vs Different)
  {
    id: 'speed_pair_comparison',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 15,
    generate(prng, difficulty = 2) {
      const len = difficulty === 1 ? 6 : difficulty === 2 ? 8 : 10;
      const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
      let strA = '';
      for (let i = 0; i < len; i++) {
        strA += chars[prng.int(0, chars.length - 1)];
      }

      const isIdentical = prng.next() > 0.5;
      let strB = strA;
      if (!isIdentical) {
        // Change exactly 1 character
        const changeIdx = prng.int(0, len - 1);
        let newChar = chars[prng.int(0, chars.length - 1)];
        while (newChar === strA[changeIdx]) {
          newChar = chars[prng.int(0, chars.length - 1)];
        }
        strB = strA.substring(0, changeIdx) + newChar + strA.substring(changeIdx + 1);
      }

      const stimulusHtml = `
        <div class="iq-string-compare-board" role="region" aria-label="مقارنة السلسلتين">
          <div class="iq-str-row str-a">${strA}</div>
          <div class="iq-str-divider"></div>
          <div class="iq-str-row str-b">${strB}</div>
        </div>
      `;

      const allChoices = [
        { text_ar: 'متطابقتان تمامًا', text_en: 'Identical (Same)', isCorrect: isIdentical },
        { text_ar: 'مختلفتان', text_en: 'Different', isCorrect: !isIdentical }
      ];

      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.text_ar,
        text_en: c.text_en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'قارن بين سلسلتي الرموز بأسرع ما يمكنك: هل هما متطابقتان تمامًا أم مختلفتان؟',
        questionText_en: 'Compare both character strings as rapidly as possible: Are they identical or different?',
        stimulusHtml,
        options,
        correctIndex,
        explanation_ar: isIdentical ? 'السلسلتان متطابقتان في جميع الخانات والحروف دون أي اختلاف.' : 'السلسلتان مختلفتان في أحد الحروف/الأرقام.',
        explanation_en: isIdentical ? 'Both character strings are completely identical in all positions.' : 'The strings differ by a character.'
      };
    }
  },

  // 3. Directional Flanker Arrow Task
  {
    id: 'speed_directional_flanker',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 15,
    generate(prng, difficulty = 2) {
      // Center arrow direction: Left or Right
      const centerIsLeft = prng.next() > 0.5;
      const flankersMatch = prng.next() > 0.5; // Congruent vs Incongruent

      const centerSym = centerIsLeft ? '◀' : '▶';
      const flankerSym = flankersMatch ? centerSym : (centerIsLeft ? '▶' : '◀');

      const stimulusSvg = `<svg class="iq-svg-flanker" viewBox="0 0 340 80" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="340" height="80" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        <text x="170" y="50" fill="#f8fafc" font-size="28" font-weight="bold" text-anchor="middle">
          ${flankerSym}  ${flankerSym}  <tspan fill="#83C8F0">${centerSym}</tspan>  ${flankerSym}  ${flankerSym}
        </text>
      </svg>`;

      const allChoices = [
        { text_ar: 'السهم الأوسط يشير إلى اليسار', text_en: 'Center arrow points Left', isCorrect: centerIsLeft },
        { text_ar: 'السهم الأوسط يشير إلى اليمين', text_en: 'Center arrow points Right', isCorrect: !centerIsLeft }
      ];

      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.text_ar,
        text_en: c.text_en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تجاهل الأسهم المحيطة وحدد اتجاه السهم الأزرق الأوسط فقط:',
        questionText_en: 'Ignore the flanking distraction arrows and report the orientation of the central blue arrow:',
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `السهم الأوسط يتجه نحو (${centerIsLeft ? 'اليسار' : 'اليمين'}).`,
        explanation_en: `The central arrow points (${centerIsLeft ? 'Left' : 'Right'}).`
      };
    }
  },

  // 4. Target Frequency Counting (Count specific symbols among varied shapes)
  {
    id: 'speed_target_frequency_count',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 25,
    generate(prng, difficulty = 2) {
      const charPool = [
        { char: '★', ar: 'النجوم (★)', en: 'stars (★)', unit_ar: 'نجمات', unit_en: 'stars' },
        { char: '●', ar: 'الدوائر (●)', en: 'circles (●)', unit_ar: 'دوائر', unit_en: 'circles' },
        { char: '▲', ar: 'المثلثات (▲)', en: 'triangles (▲)', unit_ar: 'مثلثات', unit_en: 'triangles' },
        { char: '■', ar: 'المربعات (■)', en: 'squares (■)', unit_ar: 'مربعات', unit_en: 'squares' },
        { char: '◆', ar: 'المعينات (◆)', en: 'diamonds (◆)', unit_ar: 'معينات', unit_en: 'diamonds' },
        { char: '✚', ar: 'علامات الزائد (✚)', en: 'crosses (✚)', unit_ar: 'علامات زائد', unit_en: 'crosses' }
      ];

      const chosenItems = prng.sample(charPool, 5);
      const targetItem = chosenItems[0];
      const otherChars = chosenItems.slice(1).map(c => c.char);

      const targetCount = prng.int(4, 7);
      const totalSymbols = 20;

      const symbols = Array(targetCount).fill(targetItem.char);
      while (symbols.length < totalSymbols) {
        symbols.push(prng.pick(otherChars));
      }
      const shuffled = prng.shuffle(symbols);

      let gridText = '';
      for (let r = 0; r < 4; r++) {
        const row = shuffled.slice(r * 5, (r + 1) * 5).join('    ');
        gridText += `<text x="160" y="${32 + r * 28}" fill="#f8fafc" font-size="18" text-anchor="middle">${row}</text>`;
      }

      const stimulusSvg = `<svg class="iq-svg-count-cluster" viewBox="0 0 320 140" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="320" height="140" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        ${gridText}
      </svg>`;

      const correctVal = targetCount;
      const distractors = [correctVal - 1, correctVal + 1, correctVal + 2, correctVal - 2]
        .filter(v => v > 0 && v !== correctVal).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${val} ${targetItem.unit_ar}`,
        text_en: `${val} ${targetItem.unit_en}`,
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `احسب بسرعة وبدقة: كم عدد ${targetItem.ar} الموجودة في المجموعة أعلاه؟`,
        questionText_en: `Count rapidly and accurately: How many ${targetItem.en} appear in the cluster above?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `يحتوي الشكل على ${correctVal} من ${targetItem.ar} بالضبط.`,
        explanation_en: `The cluster contains exactly ${correctVal} ${targetItem.en}.`
      };
    }
  },

  // 5. Code-Symbol Substitution Decryption
  {
    id: 'speed_code_substitution',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 25,
    generate(prng, difficulty = 2) {
      const poolSymbols = ['▲', '●', '■', '★', '◆', '✚', '▼', '✦'];
      const poolDigits = [1, 2, 3, 4, 5, 6, 7, 8, 9];
      const chosenSymbols = prng.sample(poolSymbols, 4);
      const chosenDigits = prng.sample(poolDigits, 4);

      const key = chosenSymbols.map((sym, i) => ({ sym, num: chosenDigits[i] }));

      const querySymbols = prng.sample(key, 4);
      const correctCode = querySymbols.map(k => k.num).join(' ');

      const candidateDist = [
        querySymbols.map((k, i) => chosenDigits[(i + 1) % 4]).join(' '),
        [...querySymbols].reverse().map(k => k.num).join(' '),
        querySymbols.map((k, i) => chosenDigits[(i + 2) % 4]).join(' '),
        querySymbols.map((k, i) => chosenDigits[(i + 3) % 4]).join(' '),
        querySymbols.map((k, i) => (k.num % 9) + 1).join(' ')
      ];
      const seenCodes = new Set([correctCode]);
      const distractors = [];
      for (const d of candidateDist) {
        if (!seenCodes.has(d)) {
          seenCodes.add(d);
          distractors.push(d);
          if (distractors.length === 3) break;
        }
      }

      const allChoices = prng.shuffle([correctCode, ...distractors]);

      const correctIndex = allChoices.indexOf(correctCode);

      const stimulusHtml = `
        <div class="iq-code-subst-board" role="region" aria-label="شفرة الرموز والأرقام">
          <div class="iq-code-key-row">
            <span class="iq-code-key-label">مفتاح الشفرة:</span>
            <div class="iq-code-tokens">
              ${key.map(k => `<span class="iq-code-token">${k.sym} = ${k.num}</span>`).join('')}
            </div>
          </div>
          <div class="iq-code-query-row">
            <span class="iq-code-query-label">الرموز المطلوب تشفيرها:</span>
            <div class="iq-code-query-symbols">
              ${querySymbols.map(s => `<span class="iq-query-symbol">${s.sym}</span>`).join('')}
            </div>
          </div>
        </div>
      `;

      const options = allChoices.map((code, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: code,
        text_en: code
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'باستخدام جدول شفرة الرموز، ما هو الكود الرقمي المقابل لتسلسل الرموز المطلوب؟',
        questionText_en: 'Using the symbol code key, what is the numerical translation of the given symbol sequence?',
        stimulusHtml,
        options,
        correctIndex,
        explanation_ar: `بالتعويض عن كل رمز بقيمته من المفتاح نحصل على الكود: ${correctCode}.`,
        explanation_en: `Substituting each symbol according to the key yields: ${correctCode}.`
      };
    }
  },

  // 6. Rapid Visual Match (Exact duplicate of rotated reference)
  {
    id: 'speed_rapid_visual_match',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 2) {
      const angles = [0, 45, 90, 135, 180, 225, 270, 315];
      const targetAngle = prng.pick(angles);
      const isArrow = prng.next() > 0.5;

      const drawRef = (angle) => (s) => isArrow ? DrawShapes.arrow(s, angle, '#83C8F0') : DrawShapes.triangle(s, true, 'currentColor', '#83C8F0', angle);

      const stimulusSvg = `<svg class="iq-svg-match" viewBox="0 0 140 100" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="140" height="100" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        <g transform="translate(70, 50)">${drawRef(targetAngle)(60)}</g>
      </svg>`;

      const otherAngles = angles.filter(a => a !== targetAngle);
      const chosenDistractors = prng.sample(otherAngles, 3);

      const allAngles = prng.shuffle([targetAngle, ...chosenDistractors]);
      const correctIndex = allAngles.indexOf(targetAngle);

      const options = allAngles.map((angle, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawRef(angle), 80, `Option ${idx + 1}`)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'طابق بأقصى سرعة: أي من الخيارات التالية مطابق تمامًا للشكل المرجعي (نفس الاتجاه والزاوية)؟',
        questionText_en: 'Match as quickly as possible: Which option is identical to the reference figure in orientation?',
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `الخيار المطابق هو الشكل الموجه بالزاوية ${targetAngle} درجة.`,
        explanation_en: `The matching figure points at an orientation angle of ${targetAngle}°.`
      };
    }
  },

  // 7. Spatial Stroop Conflict Resolution
  {
    id: 'speed_stroop_spatial_conflict',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 15,
    generate(prng, difficulty = 2) {
      // Incongruent position and arrow: Arrow points LEFT, but is drawn on RIGHT side of the card
      const arrowPointsLeft = prng.next() > 0.5;
      const arrowSideRight = prng.next() > 0.5;

      const arrowChar = arrowPointsLeft ? '←' : '→';
      const sideText_ar = arrowSideRight ? 'يمين' : 'يسار';

      const stimulusSvg = `<svg class="iq-svg-stroop" viewBox="0 0 340 90" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="340" height="90" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        <line x1="170" y1="10" x2="170" y2="80" stroke="rgba(255,255,255,0.1)" stroke-width="1"/>
        <text x="${arrowSideRight ? 255 : 85}" y="56" fill="#83C8F0" font-size="36" font-weight="bold" text-anchor="middle">${arrowChar}</text>
      </svg>`;

      const allChoices = [
        { text_ar: 'يشير إلى اليسار', text_en: 'Points Left', isCorrect: arrowPointsLeft },
        { text_ar: 'يشير إلى اليمين', text_en: 'Points Right', isCorrect: !arrowPointsLeft }
      ];

      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.text_ar,
        text_en: c.text_en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تجاهل موقع السهم (يمين أو يسار البطاقة) وحدد فقط الاتجاه الذي يشير إليه رأس السهم:',
        questionText_en: 'Ignore the arrow position on the card and indicate only the direction its arrowhead points:',
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `رأس السهم يشير إلى (${arrowPointsLeft ? 'اليسار' : 'اليمين'}).`,
        explanation_en: `The arrowhead points to the (${arrowPointsLeft ? 'Left' : 'Right'}).`
      };
    }
  },

  // 8. Perceptual Difference Spot (Spot differing detail)
  {
    id: 'speed_perceptual_difference_spot',
    domain: 'processing_speed',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 25,
    generate(prng, difficulty = 2) {
      // 4 numbered circles with small tick lines around their edge; one has 5 ticks instead of 4
      const oddCircleIdx = prng.int(0, 3);

      const drawClockCircle = (tickCount) => {
        let out = `<circle cx="25" cy="25" r="18" fill="rgba(102, 102, 232, 0.15)" stroke="#83C8F0" stroke-width="1.5"/>`;
        for (let i = 0; i < tickCount; i++) {
          const a = (i * 2 * Math.PI) / tickCount;
          const x1 = (25 + Math.cos(a) * 12).toFixed(1);
          const y1 = (25 + Math.sin(a) * 12).toFixed(1);
          const x2 = (25 + Math.cos(a) * 18).toFixed(1);
          const y2 = (25 + Math.sin(a) * 18).toFixed(1);
          out += `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="currentColor" stroke-width="2"/>`;
        }
        return out;
      };

      const makeStimulus = (isEnglish) => `<svg class="iq-svg-perceptual" viewBox="0 0 340 85" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="340" height="85" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        ${[0, 1, 2, 3].map(i => {
          const ticks = (i === oddCircleIdx) ? 5 : 4;
          return `
            <g transform="translate(${25 + i * 80}, 15)">
              ${drawClockCircle(ticks)}
              <text x="25" y="58" class="iq-svg-text" font-size="12" font-weight="bold" text-anchor="middle">${isEnglish ? `Fig ${i + 1}` : `شكل ${i + 1}`}</text>
            </g>
          `;
        }).join('')}
      </svg>`;

      const allChoices = [0, 1, 2, 3];
      const correctIndex = oddCircleIdx;

      const options = allChoices.map((i, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `شكل ${i + 1}`,
        text_en: `Figure ${i + 1}`
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'دقق في تفاصيل الأشكال الأربعة بأسرع وقت: أي شكل يحتوي على عدد خطوط شعاعية مختلف عن باقي الأشكال؟',
        questionText_en: 'Inspect the four figures closely: Which figure has a different number of internal spoke ticks?',
        stimulusHtml: makeStimulus(false),
        stimulusHtml_ar: makeStimulus(false),
        stimulusHtml_en: makeStimulus(true),
        options,
        correctIndex,
        explanation_ar: `الشكل ${oddCircleIdx + 1} يحتوي على 5 خطوط بينما تحتوي باقي الأشكال على 4 خطوط فقط.`,
        explanation_en: `Figure ${oddCircleIdx + 1} contains 5 spoke ticks, whereas all others have exactly 4.`
      };
    }
  }
];
